import React from "react";
import { getStatusColors } from "../../../utils/function";
import { FileText, XCircle } from "lucide-react";
import WhatsappIcon from "../../../assets/images/whatsapp.png";
interface TemplateModalProps {
  selectedTemplate: any;
  setSelectedTemplate: (template: any) => void;
}

const TemplateModal: React.FC<TemplateModalProps> = ({
  selectedTemplate,
  setSelectedTemplate,
}) => {
  return (
    <>
      {/* Template Detail Modal */}
      {selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {selectedTemplate.name}
                    </h2>
                    <div className="flex items-center space-x-2 mt-1">
                      <span
                        className={`text-xs px-2 py-1 rounded-full font-medium bg-${
                          getStatusColors(selectedTemplate.status || "") ===
                          "green"
                            ? "emerald"
                            : getStatusColors(selectedTemplate.status || "") ===
                              "yellow"
                            ? "amber"
                            : "red"
                        }-100 text-${
                          getStatusColors(selectedTemplate.status || "") ===
                          "green"
                            ? "emerald"
                            : getStatusColors(selectedTemplate.status || "") ===
                              "yellow"
                            ? "amber"
                            : "red"
                        }-700`}
                      >
                        {selectedTemplate.status}
                      </span>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                        {selectedTemplate.category}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedTemplate(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 grid lg:grid-cols-2 gap-6">
              {/* Template JSON */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Template Structure
                </h3>
                <div className="bg-gray-900 rounded-xl p-4 overflow-x-auto">
                  <pre className="text-sm text-green-400 whitespace-pre-wrap">
                    {JSON.stringify(selectedTemplate, null, 2)}
                  </pre>
                </div>
              </div>

              {/* Template Preview */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  WhatsApp Preview
                </h3>
                <div className="bg-gradient-to-b from-emerald-500 to-emerald-600 rounded-t-2xl p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center">
                      <img
                        src={WhatsappIcon}
                        alt="WhatsApp"
                        className="absolute w-8 h-8"
                      />
                    </div>
                    <span className="text-white font-medium">
                      Your Business
                    </span>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-b-2xl border border-gray-200 space-y-3">
                  {selectedTemplate.header && (
                    <div className="bg-white p-3 rounded-lg shadow-sm">
                      <div className="text-sm font-semibold text-gray-900">
                        {selectedTemplate.header.text?.replace(
                          /\{\{1\}\}/g,
                          "YourBrand"
                        )}
                      </div>
                    </div>
                  )}

                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <div className="text-sm text-gray-800 whitespace-pre-line">
                      {selectedTemplate.body.text
                        .replace(/\{\{1\}\}/g, "John")
                        .replace(/\{\{2\}\}/g, "#12345")
                        .replace(/\{\{3\}\}/g, "TRK123456")}
                    </div>
                  </div>

                  {selectedTemplate.buttons &&
                    selectedTemplate.buttons.length > 0 && (
                      <div className="space-y-2">
                        {selectedTemplate.buttons.map(
                          (button: any, index: any) => (
                            <button
                              key={index}
                              className="w-full p-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors"
                            >
                              {button.text}
                            </button>
                          )
                        )}
                      </div>
                    )}

                  {selectedTemplate.footer && (
                    <div className="text-xs text-gray-500 mt-2 pt-2 border-t">
                      {selectedTemplate.footer.text}
                    </div>
                  )}
                </div>

                {/* Template Stats */}
                <div className="mt-4 p-4 bg-indigo-50 rounded-xl">
                  <h4 className="font-medium text-indigo-900 mb-3">
                    Template Analytics
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-indigo-600">Messages Sent:</span>
                      <div className="font-semibold text-indigo-900">
                        {selectedTemplate.usage?.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <span className="text-indigo-600">Created:</span>
                      <div className="font-semibold text-indigo-900">
                        {selectedTemplate.createdAt &&
                          new Date(
                            selectedTemplate.createdAt
                          ).toLocaleDateString()}
                      </div>
                    </div>
                    <div>
                      <span className="text-indigo-600">Language:</span>
                      <div className="font-semibold text-indigo-900">
                        {selectedTemplate.language.toUpperCase()}
                      </div>
                    </div>
                    <div>
                      <span className="text-indigo-600">Status:</span>
                      <div
                        className={`font-semibold text-${
                          getStatusColors(selectedTemplate.status || "") ===
                          "green"
                            ? "emerald"
                            : getStatusColors(selectedTemplate.status || "") ===
                              "yellow"
                            ? "amber"
                            : "red"
                        }-700`}
                      >
                        {selectedTemplate.status}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TemplateModal;
